import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Alert } from "@mui/material";
import { toast } from "react-toastify";
import {
  useProgramCategories,
  useResourceCategories,
} from "../../hooks/useRedux";
import CategoryForm from "./CategoryForm";
import { Category } from "../../store/slices/types";

const EditCategory: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isProgramCategories = location.pathname.includes("program-categories");
  const isResourceCategories = location.pathname.includes("resource-categories");

  const programCategoriesHook = useProgramCategories();
  const resourceCategoriesHook = useResourceCategories();

  const {
    categories,
    updateCategory,
    loading,
  } = isProgramCategories ? programCategoriesHook : resourceCategoriesHook;

  const category = categories.find(
    (c) => (c._id || c.id) === id
  ) as Category | undefined;

  const handleSave = async (categoryData: Partial<Category>) => {
    if (!id) {
      toast.error("Invalid category ID");
      return;
    }

    try {
      await updateCategory(id, categoryData);
      toast.success("category is updated");
      navigate(
        isProgramCategories ? "/program-categories" : "/resource-categories"
      );
    } catch (error) {
      console.error("Failed to update category:", error);
      toast.error(
        `❌ Failed to update category: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const handleCancel = () => {
    navigate(
      isProgramCategories ? "/program-categories" : "/resource-categories"
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!category) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          Category not found
        </Alert>
      </Box>
    );
  }

  const categoryType = isProgramCategories ? "Program" : "Resource";

  return (
    <Box>
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          mb: 4,
        }}
      >
        <Typography variant='h4' fontWeight='700' gutterBottom>
          Edit {categoryType} Category
        </Typography>
        <Typography variant='body1' sx={{ opacity: 0.9 }}>
          Update your {categoryType.toLowerCase()} category
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <CategoryForm
          category={category}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </Paper>
    </Box>
  );
};

export default EditCategory;

